import { CalendarOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const formatCountdown = (seconds, t) => {
  if (seconds <= 0) return null;
  if (seconds < 60) return t("meeting_reminder.soon");

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} ${t("meeting_reminder.hour")}`);
  if (minutes > 0) parts.push(`${minutes} ${t("meeting_reminder.minute")}`);
  if (secs > 0 && hours === 0) parts.push(`${secs} ${t("meeting_reminder.second")}`);

  return `${parts.join(" ")} ${t("meeting_reminder.countdown_suffix")}`;
};

const MeetingReminderNotice = ({
  contactName,
  contactCompany,
  date,
  startTime,
  endTime,
  onClose,
  onClick,
}) => {
  const { t } = useTranslation();

  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    const target = dayjs(`${date} ${startTime}`, "YYYY-MM-DD HH:mm:ss");
    return Math.max(0, target.diff(dayjs(), "second"));
  });

  useEffect(() => {
    if (remainingSeconds <= 0) {
      onClose();
      return;
    }
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedDate = dayjs(`${date} ${startTime}`, "YYYY-MM-DD HH:mm:ss").format(
    "DD MMM YYYY HH:mm"
  );
  const formattedEnd = dayjs(`${date} ${endTime}`, "YYYY-MM-DD HH:mm:ss").format("HH:mm");
  const countdown = formatCountdown(remainingSeconds, t);

  const isHDay = remainingSeconds <= 86400;
  const urgentGradient = isHDay
    ? "from-orange-500 via-red-500 to-rose-600"
    : "from-amber-400 via-orange-500 to-orange-600";

  return (
    <div
      className={`pointer-events-auto w-[340px] rounded-2xl bg-gradient-to-br ${urgentGradient} p-[2px] shadow-2xl`}
    >
      <div className="relative rounded-2xl bg-white/10 backdrop-blur-sm text-white">
        {/* Close button */}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute right-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
        >
          <CloseOutlined style={{ fontSize: 11 }} />
        </button>

        {/* Clickable body */}
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => e.key === "Enter" && onClick?.()}
          className="cursor-pointer rounded-2xl px-4 py-3 hover:bg-white/10 transition-colors"
        >
          {/* Header */}
          <div className="mb-2 flex items-center gap-2 pr-7">
            <CalendarOutlined className="text-white/90 text-base shrink-0" />
            <span className="text-xs font-semibold uppercase tracking-widest text-white/80">
              {t("meeting_reminder.title")}
            </span>
          </div>

          {/* Contact info */}
          <p className="mb-0.5 text-base font-bold leading-tight">
            {contactName}
            {contactCompany ? ` (${contactCompany})` : ""}
          </p>

          {/* Date/time */}
          <p className="mb-2 text-sm text-white/80">
            {formattedDate} – {formattedEnd}
          </p>

          {/* Countdown */}
          <div className="rounded-xl bg-white/20 px-3 py-1.5 text-center">
            <span className="text-sm font-semibold tracking-wide">
              {countdown ?? t("meeting_reminder.now")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingReminderNotice;
